/* UAG 2026-09-13: align natural text rows without truncating product names.
 * Re-measure after font loading, responsive changes and AJAX/editor updates.
 * CSS keeps the native layout when JavaScript is unavailable. */
(() => {
  const properties = ['--uag-card-title-height', '--uag-card-subtitle-height', '--uag-card-missing-subtitle'];
  const observed = new WeakMap();
  let pending = false;
  const resize = new ResizeObserver((entries) => {
    let changed = false;
    for (const { target, contentRect } of entries) {
      const width = Math.round(contentRect.width);
      if (observed.get(target) === width) continue;
      observed.set(target, width);
      changed = true;
    }
    if (changed) schedule();
  });
  const grids = new Set();
  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(align);
  }
  function align() {
    pending = false;
    const cards = [...document.querySelectorAll('product-card.card--product')];
    // Clear every prior measurement before reading the new natural layout.
    for (const card of cards) for (const property of properties) card.style.removeProperty(property);
    const groups = new Map();
    for (const card of cards) {
      const grid = card.closest('.grid');
      if (!grid || card.closest('[data-layout="list"]') || !card.getClientRects().length || !card.clientWidth) continue;
      if (!grids.has(grid)) { grids.add(grid); resize.observe(grid); }
      if (!groups.has(grid)) groups.set(grid, new Map());
      const row = Math.round(card.getBoundingClientRect().top);
      const rows = groups.get(grid);
      if (!rows.has(row)) rows.set(row, []);
      rows.get(row).push(card);
    }
    for (const grid of grids) {
      if (!grid.isConnected) { resize.unobserve(grid); grids.delete(grid); }
    }
    const updates = [];
    for (const rows of groups.values()) for (const row of rows.values()) {
      if (row.length < 2) continue;
      const measured = row.map(card => {
        const title = card.querySelector('.card__title');
        const subtitle = card.querySelector('.card__subtitle');
        const css = subtitle && getComputedStyle(subtitle);
        return {card, title: title?.getBoundingClientRect().height || 0, subtitle,
          subtitleHeight: subtitle?.getBoundingClientRect().height || 0,
          subtitleMargins: css ? parseFloat(css.marginTop) + parseFloat(css.marginBottom) : 0};
      });
      const titleHeight = Math.max(...measured.map(x => x.title));
      const subtitleHeight = Math.max(...measured.map(x => x.subtitleHeight));
      const subtitleMargins = Math.max(...measured.map(x => x.subtitleMargins));
      for (const item of measured) updates.push({ ...item, titleHeight, subtitleHeight, subtitleMargins });
    }
    for (const item of updates) {
      item.card.style.setProperty('--uag-card-title-height', `${item.titleHeight}px`);
      item.card.style.setProperty('--uag-card-subtitle-height', `${item.subtitleHeight}px`);
      if (!item.subtitle) item.card.style.setProperty('--uag-card-missing-subtitle', `${item.subtitleHeight + item.subtitleMargins}px`);
    }
  }
  function start() {
    new MutationObserver(records => {
      if (records.some(record =>
        record.type === 'attributes' ||
        (record.target instanceof Element && record.target.closest('product-card')) ||
        [...record.addedNodes, ...record.removedNodes].some(node => node instanceof Element && (node.matches('product-card') || node.querySelector('product-card')))
      )) schedule();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-layout'] });
    document.addEventListener('shopify:section:load', schedule);
    window.addEventListener('resize', schedule, { passive: true });
    document.fonts?.ready.then(schedule);
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
